import { useEffect, useState, type ReactNode } from 'react';
import type { AdminAccountRef, AdminDeviceRef, Page } from '@mantelpiece/shared';
import { formatCount } from '../lib/format';
import { go, href } from '../lib/route';
import type { Loaded } from '../lib/useLoad';

/* ------------------------------------------------------------- linking ---- */

export const AccountLink = ({ account }: { readonly account: AdminAccountRef | null }) =>
  account ? (
    <a className="ref" href={href(`/accounts/${account.id}`)}>
      <span className="ref-name">{account.name}</span>
      <span className="ref-meta">{account.email}</span>
    </a>
  ) : (
    <span className="ref ref-none">nobody</span>
  );

export const FrameLink = ({ device }: { readonly device: AdminDeviceRef }) => (
  <a className="ref" href={href(`/frames/${device.id}`)}>
    <span className="ref-name">{device.label}</span>
  </a>
);

/* ------------------------------------------------------------- loading ---- */

/**
 * Renders the loaded thing, or the reason it is not here. A stale page stays
 * on screen under a "loading" note while the next one comes, so paging never
 * flashes the table away.
 */
export const Loading = <T,>({
  loaded,
  children,
}: {
  readonly loaded: Loaded<T>;
  readonly children: (data: T) => ReactNode;
}) => (
  <>
    {loaded.state === 'error' && (
      <p className="alert" role="alert">
        {loaded.message}
      </p>
    )}
    {loaded.state === 'loading' && loaded.data === null && (
      <p className="note" aria-live="polite">
        Loading…
      </p>
    )}
    {loaded.data !== null && (
      <div aria-busy={loaded.state === 'loading'} className="loaded">
        {children(loaded.data)}
      </div>
    )}
  </>
);

/* -------------------------------------------------------------- paging ---- */

interface PagerProps {
  readonly page: Page<unknown>;
  readonly onOffset: (offset: number) => void;
  readonly noun: string;
}

export const Pager = ({ page, onOffset, noun }: PagerProps) => {
  const from = page.total === 0 ? 0 : page.offset + 1;
  const to = Math.min(page.total, page.offset + page.items.length);
  const hasPrev = page.offset > 0;
  const hasNext = page.offset + page.limit < page.total;
  return (
    <nav className="pager" aria-label="Pages">
      <p className="pager-count">
        {page.total === 0
          ? `No ${noun}`
          : `${formatCount(from)}–${formatCount(to)} of ${formatCount(page.total)} ${noun}`}
      </p>
      <div className="row-tight">
        <button
          type="button"
          className="button button-quiet button-small"
          disabled={!hasPrev}
          onClick={() => onOffset(Math.max(0, page.offset - page.limit))}
        >
          Previous
        </button>
        <button
          type="button"
          className="button button-quiet button-small"
          disabled={!hasNext}
          onClick={() => onOffset(page.offset + page.limit)}
        >
          Next
        </button>
      </div>
    </nav>
  );
};

/* -------------------------------------------------------------- search ---- */

interface SearchProps {
  readonly value: string;
  readonly placeholder: string;
  readonly onSearch: (q: string) => void;
}

/** A search box that commits on Enter or after a pause, never per keystroke. */
export const Search = ({ value, placeholder, onSearch }: SearchProps) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onSearch(draft), 400);
    return () => window.clearTimeout(timer);
  }, [draft, value, onSearch]);

  return (
    <form
      className="search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(draft);
      }}
    >
      <label className="field field-inline">
        <span className="visually-hidden">Search</span>
        <input
          className="input"
          type="search"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
        />
      </label>
    </form>
  );
};

/* ---------------------------------------------------------------- stats --- */

export const Stat = ({
  label,
  value,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string | undefined;
}) => (
  <div className="stat">
    <p className="stat-label">{label}</p>
    <p className="stat-value">{value}</p>
    {note && <p className="stat-note">{note}</p>}
  </div>
);

/* ---------------------------------------------------------------- misc ---- */

export const Pill = ({
  tone,
  children,
}: {
  readonly tone?: 'ok' | 'wait' | 'bad' | undefined;
  readonly children: ReactNode;
}) => (
  <span className="pill" data-tone={tone}>
    {children}
  </span>
);

export const YesNo = ({ value }: { readonly value: boolean }) => (
  <span className="yesno" data-value={value ? 'yes' : 'no'}>
    {value ? 'yes' : 'no'}
  </span>
);

export const Crumb = ({ label, to }: { readonly label: string; readonly to: string }) => (
  <p className="crumb">
    <button type="button" className="linky" onClick={() => go(to)}>
      ← {label}
    </button>
  </p>
);

/** A description-list row: a label and its value. */
export const Fact = ({ label, children }: { readonly label: string; readonly children: ReactNode }) => (
  <div className="fact">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);
